import type { TodoStatus, TodoPriority, RecurringTodoFrequency } from '@techgitter/contracts';

type ChipColor = 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

export const STATUS_OPTIONS: { value: TodoStatus; label: string; color: ChipColor }[] = [
  { value: 'open', label: 'Open', color: 'info' },
  { value: 'in-progress', label: 'In Progress', color: 'primary' },
  { value: 'deferred', label: 'Deferred', color: 'default' },
  { value: 'scheduled', label: 'Scheduled', color: 'secondary' },
  { value: 'blocked', label: 'Blocked', color: 'error' },
  { value: 'done', label: 'Done', color: 'success' },
];

export const PRIORITY_OPTIONS: { value: TodoPriority; label: string; color: ChipColor }[] = [
  { value: 'low', label: 'Low', color: 'default' },
  { value: 'medium', label: 'Medium', color: 'warning' },
  { value: 'high', label: 'High', color: 'error' },
];

export const FREQUENCY_OPTIONS: { value: RecurringTodoFrequency; label: string }[] = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Yearly', label: 'Yearly' },
];

const STATUS_MAP = new Map(STATUS_OPTIONS.map((o) => [o.value, o]));
const PRIORITY_MAP = new Map(PRIORITY_OPTIONS.map((o) => [o.value, o]));

export function statusLabel(status: TodoStatus): string {
  return STATUS_MAP.get(status)?.label ?? status;
}

export function statusColor(status: TodoStatus): ChipColor {
  return STATUS_MAP.get(status)?.color ?? 'default';
}

export function priorityLabel(priority: TodoPriority): string {
  return PRIORITY_MAP.get(priority)?.label ?? priority;
}

export function priorityColor(priority: TodoPriority): ChipColor {
  return PRIORITY_MAP.get(priority)?.color ?? 'default';
}

/** Format an ISO timestamp as a short Australia/Sydney date-time, or '—'. */
export function formatDueDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-AU', {
    timeZone: 'Australia/Sydney',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Convert an ISO timestamp to a value for <input type="datetime-local"> (local wall time). */
export function toDateTimeLocalValue(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Convert a <input type="datetime-local"> value back to an ISO string, or undefined. */
export function fromDateTimeLocalValue(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** Format an ISO date (YYYY-MM-DD or full) as a short Australia/Sydney date, or '—'. */
export function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    timeZone: 'Australia/Sydney',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Take the YYYY-MM-DD portion of an ISO value for <input type="date">, or ''. */
export function toDateInputValue(iso?: string): string {
  return iso ? String(iso).slice(0, 10) : '';
}
